import { useCallback } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { InfiniteData, QueryClient } from '@tanstack/react-query';

import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { maLoi } from '../../../lib/errors';
import type { ApiError } from '../../../lib/errors';
import { baoCaoDoanhNghiepRepository as repo } from '../../../lib/repositories/bao-cao-doanh-nghiep.repository';
import { KHOA_LOI_MA, MOT_TRANG } from '../constants';
import type {
  Category,
  DocumentFields,
  DocumentItem,
  DocumentPage,
  ModuleId,
  ModulePayload,
  QueryDocuments,
  UpdateModulePayload,
} from '../types';

/**
 * Khoá cache: mọi thứ của module nằm dưới `['bao-cao']`, vô hiệu hoá theo nhánh.
 *
 * ⚠️ Nguồn **phải** nằm trong khoá. Thiếu nó thì đổi nguồn vẫn nhận lại dữ liệu nguồn cũ từ cache —
 * màn hình nói một đằng, kho chứa một nẻo, và người dùng bấm Lưu lên số liệu của nguồn mình không định sửa.
 */
export const KHOA_BAO_CAO = {
  goc: ['bao-cao'] as const,
  phanHe: ['bao-cao', 'phan-he'] as const,
  phanHeCua: (nguon: string | undefined, id: ModuleId) => ['bao-cao', 'phan-he', nguon ?? null, id] as const,
  taiLieu: ['bao-cao', 'tai-lieu'] as const,
  taiLieuLoc: (q: Omit<QueryDocuments, 'cursor' | 'limit'>) => ['bao-cao', 'tai-lieu', q] as const,
  thuMuc: ['bao-cao', 'thu-muc'] as const,
  thuMucCua: (nguon: string | undefined) => ['bao-cao', 'thu-muc', nguon ?? null] as const,
  chuaPhanLoai: (nguon: string | undefined) => ['bao-cao', 'thu-muc', nguon ?? null, 'chua-phan-loai'] as const,
  nguon: ['bao-cao', 'nguon'] as const,
};

/** 4xx là câu trả lời của server, không phải trục trặc: thử lại chỉ bắt người dùng chờ thêm. */
export function thuLai(soLanDaThu: number, err: unknown): boolean {
  const status = (err as ApiError | undefined)?.status;
  if (status !== undefined && status >= 400 && status < 500) return false;
  return soLanDaThu < 1;
}

/** Phân hệ chưa nhập số liệu: `GET /modules/{id}` trả `404` — không phải hỏng. */
export function laChuaNhap(err: unknown): boolean {
  return (err as ApiError | null | undefined)?.status === 404;
}

/**
 * Câu lỗi cho người dùng: mã riêng của module (`BAO_CAO_*`) có câu nói cách xử lý trong catalog; còn lại
 * theo `useLoi` (mã chung, hoặc `detail` nguyên văn của backend).
 */
export function useLoiBaoCao(): (err: unknown) => string {
  const { t } = useT();
  const dichLoi = useLoi();
  return useCallback(
    (err: unknown) => {
      const khoa = KHOA_LOI_MA[maLoi(err) ?? ''];
      return khoa ? t(khoa) : dichLoi(err);
    },
    [t, dichLoi],
  );
}

// ─────────────── Phân hệ ───────────────

/**
 * Số liệu một phân hệ của nguồn đang xem. `sanSang` false khi chưa biết nguồn: gọi sớm sẽ nhận nguồn mặc
 * định rồi ngay sau đó gọi lại bằng nguồn thật — một nhịp nháy dữ liệu sai.
 */
export function usePhanHe(id: ModuleId, nguon: string | undefined, sanSang: boolean, duocDoc: boolean) {
  return useQuery<ModulePayload, ApiError>({
    queryKey: KHOA_BAO_CAO.phanHeCua(nguon, id),
    queryFn: () => repo.phanHe(id, nguon),
    enabled: sanSang && duocDoc,
    staleTime: 30_000,
    retry: thuLai,
  });
}

/** Lưu trọn gói; ghi phản hồi của server vào cache để màn vẽ lại từ đúng thứ server đã nhận. */
export function useLuuPhanHe(id: ModuleId, nguon: string | undefined) {
  const qc = useQueryClient();
  return useMutation<ModulePayload, ApiError, UpdateModulePayload>({
    mutationFn: (than) => repo.luuPhanHe(id, than, nguon),
    onSuccess: (moi) => {
      qc.setQueryData(KHOA_BAO_CAO.phanHeCua(nguon, id), moi);
    },
  });
}

// ─────────────── Tài liệu ───────────────

/** Danh sách tài liệu theo bộ lọc, phân trang cursor bằng nút "Tải thêm". */
export function useDanhSachTaiLieu(loc: Omit<QueryDocuments, 'cursor' | 'limit'>, bat: boolean) {
  const query = useInfiniteQuery<DocumentPage, ApiError, InfiniteData<DocumentPage, string | undefined>, readonly unknown[], string | undefined>({
    queryKey: KHOA_BAO_CAO.taiLieuLoc(loc),
    queryFn: ({ pageParam }) => repo.taiLieu({ ...loc, limit: MOT_TRANG, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: undefined,
    getNextPageParam: (trang) => trang.nextCursor ?? undefined,
    enabled: bat,
    staleTime: 30_000,
    retry: thuLai,
  });
  const dong = query.data?.pages.flatMap((p) => p.items) ?? [];
  const tong = query.data?.pages[0]?.total;
  return { ...query, dong, tong };
}

/** Số tài liệu đổi theo mỗi lần thêm, gỡ, chuyển: làm mới danh sách, cây (số đếm) và nguồn cùng nhau. */
export function lamMoiTaiLieu(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: KHOA_BAO_CAO.taiLieu });
  void qc.invalidateQueries({ queryKey: KHOA_BAO_CAO.thuMuc });
  void qc.invalidateQueries({ queryKey: KHOA_BAO_CAO.phanHe });
  void qc.invalidateQueries({ queryKey: KHOA_BAO_CAO.nguon });
}

export interface LuuTaiLieu {
  /** Vắng là tạo mới. */
  id?: string;
  du: DocumentFields;
  tep: File | null;
}

export function useLuuTaiLieu(nguon: string | undefined) {
  const qc = useQueryClient();
  return useMutation<DocumentItem, ApiError, LuuTaiLieu>({
    mutationFn: ({ id, du, tep }) => (id === undefined ? repo.taoTaiLieu(du, tep, nguon) : repo.suaTaiLieu(id, du, tep)),
    onSuccess: () => lamMoiTaiLieu(qc),
  });
}

/** Gỡ khỏi app, không xoá dòng. */
export function useGoTaiLieu() {
  const qc = useQueryClient();
  return useMutation<{ id: string; isPublished: boolean }, ApiError, string>({
    mutationFn: (id) => repo.goTaiLieu(id),
    onSuccess: () => lamMoiTaiLieu(qc),
  });
}

/**
 * Chuyển nhiều tài liệu sang một thư mục. Không có endpoint hàng loạt nên đây là N lời gọi `PATCH`, **tuần
 * tự**: bắn song song thì rate limit của Gateway chặn phần đuôi. Hỏng một cái thì dừng và báo số đã chuyển,
 * để người dùng biết mình đang ở trạng thái nửa vời.
 */
export function useChuyenTaiLieu() {
  const qc = useQueryClient();
  return useMutation<number, { loi: unknown; xong: number }, { ids: string[]; folderId: string | null }>({
    mutationFn: async ({ ids, folderId }) => {
      let xong = 0;
      for (const id of ids) {
        try {
          await repo.suaTaiLieu(id, { folderId });
        } catch (loi) {
          throw { loi, xong };
        }
        xong += 1;
      }
      return xong;
    },
    onSettled: () => lamMoiTaiLieu(qc),
  });
}

/**
 * Mở tài liệu: hỏi `GET /documents/{id}/open` ngay trước khi mở (tệp tải lên là URL ký 300 giây, không lưu
 * lại). Mở bằng thẻ `<a>` tạo tạm; URL `data:` của dữ liệu mẫu thì tải xuống, vì trình duyệt chặn mở tab
 * mới cho `data:`.
 */
export function useMoTaiLieu() {
  return useMutation<void, ApiError, string>({
    mutationFn: async (id) => {
      const { url } = await repo.moTaiLieu(id);
      const a = document.createElement('a');
      a.href = url;
      if (url.startsWith('data:')) a.download = '';
      else a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
  });
}

/** Danh mục đang lọc, kiểu hẹp cho ô chọn. */
export type LocDanhMuc = Category | undefined;
